/*
 * @CopyRight    : 广州仰望星空云科技有限公司
 * @Author       : Kyle
 * @Email        : linyukai@xinyu668.com
 * @CreateTime   : 2023-08-17 18:09:50
 * @Description  : 上报数据
 */
import { formatReportValue } from '@ywxk/util/handler'
import { localCache } from '@ywxk/util/localCache'
import { browser } from '@ywxk/util/browser'
import { constConf } from '@GLOBAL/constConf';
import { userReport, initReport } from '@GLOBAL/report';
import { appData } from '@GLOBAL/bridge';

const { IS_LOCAL, PROJECT, ACT_ID, FOLDER, IS_DEBUG } = constConf

// 性能统计
const getWebInfo = () => {
  return new Promise((resolve, reject) => {
    ; (window as any)?.WEBSTATS.getWebInfo((_data) => {
      const { loadPage, domReady, total, domActive, TTFB, firstPaintData, navigationStart } = _data

      const client = browser.isIOS ? 'ios' : browser.isAndroid ? 'android' : 'pc'
      const statistical_performance_time = (window as any)?.STATISTICAL_PERFORMANCE_TIME || loadPage

      const dataJSON = {
        client,
        title: FOLDER,
        page_name: location.href,
        total_load_time: Math.min(statistical_performance_time, loadPage), // 首屏加载时长
        total_load_all_time: loadPage, // 页面加载时长, PLT
        total_dom_ready_time: domReady, // 解析 DOM 树结构的时间
        total_dom_completed_time: total, // 总耗时
        total_dom_interactive_time: domActive, // DOM可交互耗时
        total_ttfb_time: TTFB, // 首字节等待时间 TTFB
        first_paint: firstPaintData.firstPaint, // 首次内容渲染时长 FCP
        first_contentful_paint: firstPaintData.firstContentfulPaint, // 首次有效渲染时长 FMP
        largest_contentful_paint: (window as any)?.lcpTime - navigationStart || 0, // 可视区域中最大的内容元素呈现到屏幕上的时间 LCP
      }
      resolve(dataJSON)
    })
  })
}

// <===================================== 帧率计算 ======================================
const rAF = function () {
  return (
    window?.requestAnimationFrame ||
    window?.webkitRequestAnimationFrame ||
    function (callback) {
      window.setTimeout(callback, 1000 / 60);
    }
  );
}();
const getFPS = () => {
  return new Promise((resolve) => {
    const start = Date.now()
    const fpsAry = []
    let lastTime = 0;
    let time = 0
    function loop() {
      const now = Date.now();
      let gap = 0;
      let fps = 0;
      if (lastTime > 0) {
        gap = now - lastTime
        fps = 1000 / gap
        fps !== Infinity && fpsAry.push(fps)
      }
      lastTime = now
      // 太少的话可能会出现同一毫秒执行两次,gap为0
      if (time >= 20) {
        const duration = now - start
        const _fps = Math.round((fpsAry.reduce((total, item) => total + item, 0)) / fpsAry.length)
        IS_DEBUG && console.log(`运行次数: ${time}; 运行时长: ${duration}ms, 平均fps: ${_fps}`);
        resolve(_fps)
      } else {
        time++
        rAF(loop);
      }
    }
    loop()
  })
}
// ====================================== 帧率计算 =====================================>



export const report = {
  /**
   * @description: 用户行为上报
   * @param      {*} eventType 事件名
   * @param      {*} params 参数对象
   * @return     {*}
   */
  user(eventType, params) {
    if (IS_LOCAL) return
    userReport.report(
      eventType,
      Object.keys(params).map(key => {
        return { key, value: formatReportValue(params[key]) }
      })
    );
  },

  // 卡顿率,低于30有明显卡顿
  async fps() {
    const fps = await getFPS()
    // 本地或者特殊机型(安卓模拟器)无需上报
    if (IS_LOCAL || String(appData?.isSpecialPhone) === 'true') return;
    initReport.report(
      'web_sys_fps',
      [
        { key: 'fps', value: formatReportValue(fps) },
        { key: 'ua', value: formatReportValue(navigator.userAgent) },
        { key: 'playerId', value: formatReportValue(appData?.id || '0') },
        { key: 'title', value: FOLDER },
        { key: 'url', value: location.href },
      ]
    );
  },

  // 性能上报
  performance: async (params) => {
    const res = await getWebInfo()
    const { readyTime, getKeyTime, id } = params
    // brand格式：品牌-型号-系统版本
    const deviceInfo = appData?.brand?.split('-') || []

    // 记录是否首次访问
    const KEY_NAME = `${PROJECT}_${FOLDER}_PERFORMANCE_ISFIRST`
    const isFirst = localCache.getItem(KEY_NAME) === null ? 1 : 0
    if (isFirst) localCache.setItem(KEY_NAME, isFirst)

    // 是否加载离线包标识 0远端 1内存 2磁盘(离线包) 3磁盘（本地缓存）
    const isOffline = appData?.isOffline >= 0 ? appData.isOffline : -1

    const data = {
      // 兼容废弃的数据
      // ajax_list: "",
      // preload_pic_list: "",
      // preload_pic_len: "",
      // preload_pic_time: "",

      // 固定数据
      app_id: PROJECT,
      webview_init_time: appData?.webviewInitTime ?? 0,
      activity_id: ACT_ID,
      act_ready_time: readyTime,
      act_real_ready_time: readyTime,
      get_key_time: getKeyTime,
      player_id: id || appData?.id || '0',
      version: appData?.version || '',
      device_brand: deviceInfo[0] || '',
      device_model: deviceInfo.length ? deviceInfo.slice(1, deviceInfo.length - 1).join('-') : '',
      device_system_version: deviceInfo.length ? deviceInfo[deviceInfo.length - 1] : '',
      is_first: isFirst,
      is_offline: isOffline,
      ...(res as any),
    }
    console.log(`${isFirst ? '首' : '二'}次加载页面;webview初始化: ${data.webview_init_time}ms;首屏渲染: ${data.total_load_time}ms;全屏渲染: ${data.total_load_all_time}ms`);
    // 本地或者特殊机型(安卓模拟器)无需上报
    if (IS_LOCAL || String(appData?.isSpecialPhone) === 'true') return;
    initReport.report('web_sys_performance', Object.keys(data).map(key => {
      return { key, value: formatReportValue(data[key]) }
    }))
  }
}
